import * as THREE from 'three';
import { VIEWPORT_CAMERA_FOV } from './viewportCameraSync.js';
import {
  BufferGeometry,
  BoxGeometry,
  CylinderGeometry,
  DoubleSide,
  Euler,
  Float32BufferAttribute,
  Group,
  LatheGeometry,
  Line,
  LineBasicMaterial,
  LineSegments,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  OctahedronGeometry,
  PlaneGeometry,
  Quaternion,
  Raycaster,
  SphereGeometry,
  TorusGeometry,
  Vector2,
  Vector3,
} from 'three';
import store from '../../redux/store';

const _raycaster = new Raycaster();
// Selection outline puts the gizmo on layer 1 — raycast both content + UI layers.
_raycaster.layers.enable(0);
_raycaster.layers.enable(1);

const _tempVector = new Vector3();
const _tempVector2 = new Vector3();
const _tempQuaternion = new Quaternion();
const _unit = {
  X: new Vector3(1, 0, 0),
  Y: new Vector3(0, 1, 0),
  Z: new Vector3(0, 0, 1),
};

const _changeEvent = { type: 'change' };
const _mouseDownEvent = { type: 'mouseDown' };
const _mouseUpEvent = { type: 'mouseUp', mode: null };
const _objectChangeEvent = { type: 'objectChange' };

/**
 * Gizmo world scale — perspective uses distance; orthographic also compensates for
 * zoom so on-screen size matches perspective at the same orbit distance.
 */
function computeTransformControlsScaleFactor(
  camera,
  worldPosition,
  cameraPosition,
  size = 0.15,
  multiplier = 1
) {
  const distance = Math.max(worldPosition.distanceTo(cameraPosition), 0.001);
  let scale = (size ?? 0.15) * (distance / 8) * multiplier;

  if (camera?.isOrthographicCamera) {
    const fovRad = THREE.MathUtils.degToRad(VIEWPORT_CAMERA_FOV);
    const frustumHeight = Math.max(
      camera.top - camera.bottom,
      typeof window !== 'undefined' ? window.innerHeight : 1,
      1
    );
    const matchedZoom =
      frustumHeight / (2 * distance * Math.tan(fovRad / 2));
    const zoom = Math.max(camera.zoom || 1, 0.001);
    scale *= matchedZoom / zoom;
  }

  return scale;
}

const _boundsBox = new THREE.Box3();
const _boundsMeshBox = new THREE.Box3();
const _boundsLocalBox = new THREE.Box3();
const _boundsCenter = new Vector3();
const _boundsSize = new Vector3();
const _boundsCorner = new Vector3();
const _boundsInvMatrix = new Matrix4();
const _boundsMeshMatrix = new Matrix4();
const _boundsWorldQuat = new Quaternion();
const _boundsWorldScale = new Vector3();

/** True when the selected node is an asset parent (GLB pivot, shape group, multi-mesh group). */
function isParentAssetSelection(object) {
  if (!object) return false;
  if (object.isMesh || object.isSkinnedMesh) return false;

  const glbType = object.glb_type ?? object.userData?.glb_type;
  if (glbType === 'ply') return false;

  if (object.deleteType === 'mesh' && object.objType === '3d') return true;
  if (object.isShapes || glbType === 'shapes' || object.userData?.isShapes) {
    return true;
  }

  if (object.userData?.isHotspot) return true;

  if (object.type === 'Group' && object.parent?.type !== 'Scene') {
    let meshCount = 0;
    object.traverse((child) => {
      if (child.isMesh || child.isSkinnedMesh) meshCount += 1;
    });
    if (meshCount > 1) return true;
  }

  return false;
}

/**
 * Parent selection → bounds over full hierarchy.
 * Child mesh selection → bounds for that mesh only.
 */
function resolveSelectionBoundsRoot(object) {
  if (!object) return null;
  if (isParentAssetSelection(object)) return object;
  return object;
}

function getMaterialHighlightMeshes() {
  return (store.getState()?.assets_object?.materialHighlightMeshes ?? []).filter(
    (mesh) => mesh && (mesh.isMesh || mesh.isSkinnedMesh)
  );
}

function isLiveObject3D(node) {
  return !!(node && typeof node.updateMatrixWorld === 'function');
}

function isSplatObject(object) {
  return (
    object?.glb_type === 'ply' || object?.userData?.glb_type === 'ply'
  );
}

function setFallbackLocalBoundsBox(size = 1) {
  const half = size / 2;
  _boundsLocalBox.makeEmpty();
  _boundsLocalBox.set(
    new Vector3(-half, -half, -half),
    new Vector3(half, half, half)
  );
  return _boundsLocalBox;
}

function disableMeshShadows(root) {
  root?.traverse?.((child) => {
    if (child.isMesh) {
      child.castShadow = false;
      child.receiveShadow = false;
    }
  });
}

class TransformControls extends Object3D {
  constructor(camera, domElement) {
    super();

    if (domElement === undefined) {
      console.warn(
        'THREE.TransformControls: The second parameter "domElement" is now mandatory.'
      );
      domElement = document;
    }

    this.isTransformControls = true;

    this.visible = false;
    this.domElement = domElement;
    this.domElement.style.touchAction = 'none';

    const _gizmo = new TransformControlsGizmo();
    this._gizmo = _gizmo;
    this.add(_gizmo);

    const _plane = new TransformControlsPlane();
    this._plane = _plane;
    this.add(_plane);

    disableMeshShadows(this);

    this._boxGroup = new Group();
    this._boxGroup.visible = false;
    this.add(this._boxGroup);
    this._boxMesh = null;
    this._points = null;
    this._lines = null;

    this._angleDisplay = document.createElement('div');
    this._angleDisplay.style.position = 'absolute';
    this._angleDisplay.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
    this._angleDisplay.style.color = '#ffffff';
    this._angleDisplay.style.padding = '6px 12px';
    this._angleDisplay.style.borderRadius = '10px';
    this._angleDisplay.style.fontFamily = 'Arial, sans-serif';
    this._angleDisplay.style.fontSize = '13px';
    this._angleDisplay.style.pointerEvents = 'none';
    this._angleDisplay.style.display = 'none';
    this._angleDisplay.style.zIndex = '1000';
    this.domElement.ownerDocument.body.appendChild(this._angleDisplay);

    this.hoveredPartName = null;

    const scope = this;

    this._unscaledBoxSize = new Vector3();
    this._unscaledBoxCenter = new Vector3();
    this._attachedObjectGeometryId = null;
    this._lastBoxRecomputeTime = 0;
    this._lastObjectId = null;
    this._lastBoundsRootId = null;
    this._lastQuaternion = new Quaternion();
    this._lastPosition = new Vector3();
    this._lastScale = new Vector3();
    this._originalGizmoColors = {};

    function defineProperty(propName, defaultValue) {
      let propValue = defaultValue;
      Object.defineProperty(scope, propName, {
        get: () => (propValue !== undefined ? propValue : defaultValue),
        set: (value) => {
          if (propValue !== value) {
            propValue = value;
            _plane[propName] = value;
            _gizmo[propName] = value;
            scope.dispatchEvent({ type: propName + '-changed', value });
            scope.dispatchEvent(_changeEvent);
          }
        },
      });
      scope[propName] = defaultValue;
      _plane[propName] = defaultValue;
      _gizmo[propName] = defaultValue;
    }

    defineProperty('camera', camera);
    defineProperty('object', undefined);
    defineProperty('enabled', true);
    defineProperty('axis', null);
    defineProperty('mode', 'translate');
    defineProperty('translationSnap', null);
    defineProperty('rotationSnap', null);
    defineProperty('scaleSnap', null);
    defineProperty('space', 'world');
    defineProperty('size', 1.3);
    defineProperty('dragging', false);
    defineProperty('showX', true);
    defineProperty('showY', true);
    defineProperty('showZ', true);
    defineProperty('showSelectionBox', true);

    const worldPosition = new Vector3();
    const worldPositionStart = new Vector3();
    const worldQuaternion = new Quaternion();
    const worldQuaternionStart = new Quaternion();
    const cameraPosition = new Vector3();
    const cameraQuaternion = new Quaternion();
    const pointStart = new Vector3();
    const pointEnd = new Vector3();
    const rotationAxis = new Vector3();
    const rotationAngle = 0;
    const eye = new Vector3();

    defineProperty('worldPosition', worldPosition);
    defineProperty('worldPositionStart', worldPositionStart);
    defineProperty('worldQuaternion', worldQuaternion);
    defineProperty('worldQuaternionStart', worldQuaternionStart);
    defineProperty('cameraPosition', cameraPosition);
    defineProperty('cameraQuaternion', cameraQuaternion);
    defineProperty('pointStart', pointStart);
    defineProperty('pointEnd', pointEnd);
    defineProperty('rotationAxis', rotationAxis);
    defineProperty('rotationAngle', rotationAngle);
    defineProperty('eye', eye);

    this._offset = new Vector3();
    this._startNorm = new Vector3();
    this._endNorm = new Vector3();
    this._cameraScale = new Vector3();

    this._parentPosition = new Vector3();
    this._parentQuaternion = new Quaternion();
    this._parentQuaternionInv = new Quaternion();
    this._parentScale = new Vector3();

    this._worldScaleStart = new Vector3();
    this._worldQuaternionInv = new Quaternion();
    this._worldScale = new Vector3();

    this._positionStart = new Vector3();
    this._quaternionStart = new Quaternion();
    this._scaleStart = new Vector3();

    this._getPointer = getPointer.bind(this);
    this._onPointerDown = onPointerDown.bind(this);
    this._onPointerHover = onPointerHover.bind(this);
    this._onPointerMove = onPointerMove.bind(this);
    this._onPointerUp = onPointerUp.bind(this);

    this.domElement.addEventListener('pointerdown', this._onPointerDown);
    this.domElement.addEventListener('pointermove', this._onPointerHover);
    this.domElement.addEventListener('pointerup', this._onPointerUp);

    // Save original colors
    this._saveOriginalColors();
  }

  _saveOriginalColors() {
    this._originalGizmoColors = {};
    this._gizmo.gizmo.children.forEach((child) => {
      if (child.name && child.material && child.material.color) {
        this._originalGizmoColors[child.name] = child.material.color.getHex();
      }
    });
  }

  _updateGizmoHighlight() {
    if (!this._gizmo?.gizmo) return;

    const isDragging = this.dragging;

    // Reset all to original colors (protect angle parts)
    this._gizmo.gizmo.children.forEach((child) => {
      if (child.tag === 'angleNeedle' || child.tag === 'angleArc') return;

      if (child.name && this._originalGizmoColors[child.name] !== undefined) {
        child.material.color.setHex(this._originalGizmoColors[child.name]);
      }
    });

    // Hover highlight → only the exact hovered part
    if (!isDragging && this.hoveredPartName) {
      this._gizmo.gizmo.children.forEach((child) => {
        if (child.tag === 'angleNeedle' || child.tag === 'angleArc') return;
        if (child.name === this.hoveredPartName && child.material) {
          child.material.color.setHex(0xffff00); // yellow on hover
        }
      });
    }

    // Dragging highlight → whole axis
    if (isDragging && this.axis) {
      this._gizmo.gizmo.children.forEach((child) => {
        if (child.tag === 'angleNeedle' || child.tag === 'angleArc') return;
        if (child.name?.startsWith(this.axis + '-') && child.material) {
          child.material.color.setHex(0xffee33); // brighter when dragging
        }
      });
    }
  }

  _createPointLineGeometry() {
    // Clear existing elements
    if (this._boxMesh) {
      this._boxGroup.remove(this._boxMesh);
      this._boxMesh.geometry?.dispose();
      this._boxMesh.material?.dispose();
      this._boxMesh = null;
    }
    if (this._points) {
      this._points.forEach((p) => {
        this._boxGroup.remove(p);
        p.geometry?.dispose();
        p.material?.dispose();
      });
      this._points = null;
    }
    if (this._lines) {
      this._boxGroup.remove(this._lines);
      this._lines.geometry?.dispose();
      this._lines.material?.dispose();
      this._lines = null;
    }

    const sizeX = Number.isFinite(this._unscaledBoxSize.x)
      ? Math.max(Math.abs(this._unscaledBoxSize.x), 0.001)
      : 0.001;
    const sizeY = Number.isFinite(this._unscaledBoxSize.y)
      ? Math.max(Math.abs(this._unscaledBoxSize.y), 0.001)
      : 0.001;
    const sizeZ = Number.isFinite(this._unscaledBoxSize.z)
      ? Math.max(Math.abs(this._unscaledBoxSize.z), 0.001)
      : 0.001;

    // ✅ Create transparent box geometry for main bounding box visual
    const boxGeometry = new BoxGeometry(sizeX, sizeY, sizeZ);
    const boxMaterial = new MeshBasicMaterial({
      color: 0x696969,
      transparent: true,
      opacity: 0,
      depthTest: false,
      depthWrite: false,
      side: DoubleSide,
    });
    this._boxMesh = new Mesh(boxGeometry, boxMaterial);
    this._boxMesh.castShadow = false;
    this._boxMesh.receiveShadow = false;
    this._boxGroup.add(this._boxMesh);

    // ✅ Create corner spheres
    const radius = 0.04;
    const sphereGeo = new SphereGeometry(radius, 16, 12);
    const sphereMat = new MeshBasicMaterial({
      color: 0x696969,
      opacity: 1,
      depthTest: false,
      depthWrite: false,
    });

    const halfX = sizeX / 2;
    const halfY = sizeY / 2;
    const halfZ = sizeZ / 2;

    const corners = [
      [-halfX, -halfY, -halfZ],
      [halfX, -halfY, -halfZ],
      [halfX, halfY, -halfZ],
      [-halfX, halfY, -halfZ],
      [-halfX, -halfY, halfZ],
      [halfX, -halfY, halfZ],
      [halfX, halfY, halfZ],
      [-halfX, halfY, halfZ],
    ];

    this._points = [];
    corners.forEach(([x, y, z]) => {
      const sphere = new Mesh(sphereGeo.clone(), sphereMat.clone());
      sphere.castShadow = false;
      sphere.receiveShadow = false;
      sphere.position.set(x, y, z);
      this._boxGroup.add(sphere);
      this._points.push(sphere);
    });

    // ✅ Create edge lines
    const edges = [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 0], // bottom face
      [4, 5],
      [5, 6],
      [6, 7],
      [7, 4], // top face
      [0, 4],
      [1, 5],
      [2, 6],
      [3, 7], // vertical edges
    ];

    const linePositions = [];
    edges.forEach(([a, b]) => {
      const [x1, y1, z1] = corners[a];
      const [x2, y2, z2] = corners[b];
      linePositions.push(x1, y1, z1, x2, y2, z2);
    });

    const lineGeometry = new BufferGeometry();
    lineGeometry.setAttribute(
      'position',
      new Float32BufferAttribute(linePositions, 3)
    );
    const lineMaterial = new LineBasicMaterial({
      color: 0x696969,
      linewidth: 2,
      depthTest: false,
      depthWrite: false,
    });
    this._lines = new LineSegments(lineGeometry, lineMaterial);
    this._boxGroup.add(this._lines);
    disableMeshShadows(this._boxGroup);
  }

  _expandMeshInLocalBounds(mesh, invRootMatrix) {
    if (!mesh) return false;

    if (mesh.isSkinnedMesh) {
      _boundsMeshBox.setFromObject(mesh);
    } else {
      if (!mesh.geometry) return false;

      const geometry = mesh.geometry;
      if (!geometry.boundingBox) {
        geometry.computeBoundingBox();
      }

      const geoBox = geometry.boundingBox;
      if (!geoBox || geoBox.isEmpty()) return false;

      _boundsMeshMatrix.multiplyMatrices(invRootMatrix, mesh.matrixWorld);

      const { min, max } = geoBox;
      for (const x of [min.x, max.x]) {
        for (const y of [min.y, max.y]) {
          for (const z of [min.z, max.z]) {
            _boundsCorner.set(x, y, z).applyMatrix4(_boundsMeshMatrix);
            _boundsLocalBox.expandByPoint(_boundsCorner);
          }
        }
      }
      return true;
    }

    const { min, max } = _boundsMeshBox;
    for (const x of [min.x, max.x]) {
      for (const y of [min.y, max.y]) {
        for (const z of [min.z, max.z]) {
          _boundsCorner.set(x, y, z).applyMatrix4(invRootMatrix);
          _boundsLocalBox.expandByPoint(_boundsCorner);
        }
      }
    }
    return true;
  }

  _collectLocalBoundsBox(object, boundsRoot) {
    _boundsLocalBox.makeEmpty();
    if (!boundsRoot) return _boundsLocalBox;

    const multiObjects = object?.userData?.isMultiSelectionPivot
      ? object.userData.multiSelectionObjects
      : null;

    if (multiObjects?.length) {
      boundsRoot.updateMatrixWorld(true);
      _boundsInvMatrix.copy(boundsRoot.matrixWorld).invert();

      multiObjects.forEach((obj) => {
        if (!obj) return;
        obj.updateMatrixWorld(true);
        _boundsBox.setFromObject(obj);
        if (_boundsBox.isEmpty()) {
          _boundsCorner.setFromMatrixPosition(obj.matrixWorld);
          _boundsBox.set(_boundsCorner, _boundsCorner);
        }
        const { min, max } = _boundsBox;
        for (const x of [min.x, max.x]) {
          for (const y of [min.y, max.y]) {
            for (const z of [min.z, max.z]) {
              _boundsCorner.set(x, y, z).applyMatrix4(_boundsInvMatrix);
              _boundsLocalBox.expandByPoint(_boundsCorner);
            }
          }
        }
      });

      return _boundsLocalBox;
    }

    if (!isLiveObject3D(boundsRoot)) {
      return setFallbackLocalBoundsBox(isSplatObject(object) ? 2 : 1);
    }

    boundsRoot.updateMatrixWorld(true);
    _boundsInvMatrix.copy(boundsRoot.matrixWorld).invert();

    const collectMesh = (mesh) => {
      if (!this._expandMeshInLocalBounds(mesh, _boundsInvMatrix)) {
        _boundsBox.setFromObject(mesh);
        const { min, max } = _boundsBox;
        for (const x of [min.x, max.x]) {
          for (const y of [min.y, max.y]) {
            for (const z of [min.z, max.z]) {
              _boundsCorner.set(x, y, z).applyMatrix4(_boundsInvMatrix);
              _boundsLocalBox.expandByPoint(_boundsCorner);
            }
          }
        }
      }
    };

    const materialMeshes = getMaterialHighlightMeshes();
    if (
      materialMeshes.length > 0 &&
      isParentAssetSelection(boundsRoot) &&
      !object?.userData?.isMultiSelectionPivot
    ) {
      materialMeshes.forEach((mesh) => {
        collectMesh(mesh);
      });

      if (!_boundsLocalBox.isEmpty()) {
        return _boundsLocalBox;
      }
    }

    if (isParentAssetSelection(object)) {
      boundsRoot.traverse((child) => {
        if ((child.isMesh || child.isSkinnedMesh) && !child.userData?.nonSelectable) collectMesh(child);
      });
    } else if ((boundsRoot.isMesh || boundsRoot.isSkinnedMesh) && !boundsRoot.userData?.nonSelectable) {
      collectMesh(boundsRoot);
    }

    if (_boundsLocalBox.isEmpty()) {
      _boundsBox.setFromObject(boundsRoot);
      const { min, max } = _boundsBox;
      for (const x of [min.x, max.x]) {
        for (const y of [min.y, max.y]) {
          for (const z of [min.z, max.z]) {
            _boundsCorner.set(x, y, z).applyMatrix4(_boundsInvMatrix);
            _boundsLocalBox.expandByPoint(_boundsCorner);
          }
        }
      }
    }

    return _boundsLocalBox;
  }

  _refreshSelectionBoxGeometry() {
    if (!this.object || !this.showSelectionBox) return;

    this.object.updateMatrixWorld(true);
    this._unscaledBoxSize.copy(this._computeUnscaledBoundingBox(this.object));
    this._createPointLineGeometry();
    this._syncSelectionBoxTransform();
    this._updateSpheresScale();
    this._boxGroup.updateMatrixWorld(true);
  }

  _computeUnscaledBoundingBox(object) {
    if (!object) {
      this._unscaledBoxCenter.set(0, 0, 0);
      return new Vector3(0.001, 0.001, 0.001);
    }

    const boundsRoot = resolveSelectionBoundsRoot(object) || object;
    this._collectLocalBoundsBox(object, boundsRoot);

    _boundsLocalBox.getSize(_boundsSize);
    _boundsLocalBox.getCenter(_boundsCenter);
    this._unscaledBoxCenter.copy(_boundsCenter);

    _boundsSize.x = Number.isFinite(_boundsSize.x)
      ? Math.max(Math.abs(_boundsSize.x), 0.001)
      : 0.001;
    _boundsSize.y = Number.isFinite(_boundsSize.y)
      ? Math.max(Math.abs(_boundsSize.y), 0.001)
      : 0.001;
    _boundsSize.z = Number.isFinite(_boundsSize.z)
      ? Math.max(Math.abs(_boundsSize.z), 0.001)
      : 0.001;

    return _boundsSize.clone();
  }

  /** Oriented box: rotates/scales with the selection (stays tight while rotating). */
  _syncSelectionBoxTransform() {
    if (!this.object) return;

    const boundsRoot = resolveSelectionBoundsRoot(this.object) || this.object;
    if (!isLiveObject3D(boundsRoot)) {
      if (!isLiveObject3D(this.object)) return;
      this._boxGroup.position.copy(this.object.position);
      this._boxGroup.quaternion.copy(this.object.quaternion);
      this._boxGroup.scale.copy(this.object.scale);
      return;
    }

    boundsRoot.updateMatrixWorld(true);

    _boundsCenter.copy(this._unscaledBoxCenter);
    boundsRoot.localToWorld(_boundsCenter);

    boundsRoot.matrixWorld.decompose(
      _tempVector,
      _boundsWorldQuat,
      _boundsWorldScale
    );

    this._boxGroup.position.copy(_boundsCenter);
    this._boxGroup.quaternion.copy(_boundsWorldQuat);
    this._boxGroup.scale.copy(_boundsWorldScale);
  }

  updateAngleDisplay() {
    if (
      this.mode !== 'rotate' ||
      !this.dragging ||
      !this.axis ||
      this.axis === 'E' ||
      this.axis === 'XYZE' ||
      !this.object
    ) {
      this._angleDisplay.style.display = 'none';
      return;
    }

    const deg = ((this.rotationAngle * 180) / Math.PI).toFixed(1);
    this._angleDisplay.textContent = `${deg}°`;

    const meter = this._gizmo.gizmo.children.find(
      (c) => c.tag === 'angleMeter'
    );
    if (!meter) {
      this._angleDisplay.style.display = 'none';
      return;
    }

    const pos = new Vector3();
    meter.getWorldPosition(pos);
    pos.add(new Vector3(0, 0.9, 0.9));
    pos.project(this.camera);

    const rect = this.domElement.getBoundingClientRect();
    const x = ((pos.x + 1) / 2) * rect.width + rect.left;
    const y = ((-pos.y + 1) / 2) * rect.height + rect.top;

    this._angleDisplay.style.left = `${x - this._angleDisplay.offsetWidth / 2}px`;
    this._angleDisplay.style.top = `${y - this._angleDisplay.offsetHeight - 14}px`;
    this._angleDisplay.style.display = 'block';
  }

  updateMatrixWorld() {
    if (this.object) {
      if (!this._isAttachedObjectVisible()) {
        this.visible = false;
        this._boxGroup.visible = false;
        this._angleDisplay.style.display = 'none';
        this.camera.updateMatrixWorld();
        super.updateMatrixWorld();
        return;
      }

      this.visible = true;

      this.object.updateMatrix();
      if (this.object.parent) {
        this.object.matrixWorld.multiplyMatrices(
          this.object.parent.matrixWorld,
          this.object.matrix
        );
        this.object.parent.matrixWorld.decompose(
          this._parentPosition,
          this._parentQuaternion,
          this._parentScale
        );
      }

      this.object.matrixWorld.decompose(
        this.worldPosition,
        this.worldQuaternion,
        this._worldScale
      );
      this._parentQuaternionInv.copy(this._parentQuaternion).invert();
      this._worldQuaternionInv.copy(this.worldQuaternion).invert();

      // Check if it's a light or light helper
      const isLight =
        this.object.isLight ||
        this.object.type === 'AmbientLight' ||
        this.object.type === 'DirectionalLight' ||
        this.object.type === 'PointLight' ||
        this.object.type === 'SpotLight' ||
        this.object.type === 'RectAreaLight' ||
        this.object.type === 'AmbientLightHelper' ||
        this.object.type === 'DirectionalLightHelper' ||
        this.object.type === 'PointLightHelper' ||
        this.object.type === 'SpotLightHelper' ||
        this.object.type === 'AreaLightHelper';

      const geoId = this.object.geometry?.id;
      const objId = this.object.id;
      const boundsRoot = resolveSelectionBoundsRoot(this.object) || this.object;
      const boundsRootId = boundsRoot.uuid;
      const q = this.object.quaternion.clone();
      const p = this.object.position.clone();
      const s = this.object.scale.clone();

      const isParticle = this.object.deleteType === 'particle';

      const transformChanged =
        this._attachedObjectGeometryId !== geoId ||
        this._lastObjectId !== objId ||
        this._lastBoundsRootId !== boundsRootId ||
        !this._lastQuaternion?.equals(q) ||
        !this._lastPosition?.equals(p) ||
        !this._lastScale?.equals(s);

      const hasGeometryChange =
        this._attachedObjectGeometryId !== geoId ||
        this._lastObjectId !== objId ||
        this._lastBoundsRootId !== boundsRootId ||
        this.object.userData?.forceBoxUpdate;

      const throttleMs = 300;
      const now = Date.now();
      const allowRecompute = transformChanged && (now - this._lastBoxRecomputeTime > throttleMs);

      // Always recompute if geometry changed, otherwise apply throttle (except for particles)
      // Skip bounding box computation for light objects
      // For particles, we skip periodic recomputation to keep the box fixed during animation
      const shouldRecomputeBox = this.showSelectionBox && !isLight &&
        (hasGeometryChange || (allowRecompute && !isParticle));

      if (shouldRecomputeBox) {
        if (this.object.userData?.forceBoxUpdate) {
          this.object.userData.forceBoxUpdate = false;
        }
        this._lastBoxRecomputeTime = now;
        this._unscaledBoxSize.copy(
          this._computeUnscaledBoundingBox(this.object)
        );
        this._attachedObjectGeometryId = geoId;
        this._lastObjectId = objId;
        this._lastBoundsRootId = boundsRootId;
        this._lastQuaternion = q.clone();
        this._lastPosition = p.clone();
        this._lastScale = s.clone();
        this._createPointLineGeometry();
      } else if (transformChanged) {
        this._attachedObjectGeometryId = geoId;
        this._lastObjectId = objId;
        this._lastBoundsRootId = boundsRootId;
        this._lastQuaternion = q.clone();
        this._lastPosition = p.clone();
        this._lastScale = s.clone();
      }

      if (this.showSelectionBox && !isLight) {
        this._syncSelectionBoxTransform();
        this._updateSpheresScale();
        this._boxGroup.updateMatrixWorld(true);
        this._boxGroup.visible = true;
      } else {
        this._boxGroup.visible = false;
      }
    } else {
      this._boxGroup.visible = false;
    }

    this.camera.updateMatrixWorld();
    this.camera.matrixWorld.decompose(
      this.cameraPosition,
      this.cameraQuaternion,
      this._cameraScale
    );

    if (this.camera.isOrthographicCamera) {
      this.camera.getWorldDirection(this.eye).negate();
    } else {
      this.eye.copy(this.cameraPosition).sub(this.worldPosition).normalize();
    }

    super.updateMatrixWorld();

    this.updateAngleDisplay();
  }

  pointerHover(pointer) {
    if (!this.object || this.dragging) return;

    _raycaster.setFromCamera(pointer, this.camera);
    const intersect = intersectObjectWithRay(this._gizmo.picker, _raycaster);

    const prevHovered = this.hoveredPartName;

    if (intersect) {
      this.hoveredPartName = intersect.object.name; // e.g. "X-arrow" or "X-sphere"
      this.axis = this.hoveredPartName.split('-')[0] || null; // "X", "Y", "Z"
    } else {
      this.hoveredPartName = null;
      this.axis = null;
    }

    if (prevHovered !== this.hoveredPartName) {
      this._updateGizmoHighlight();
    }

    this.updateAngleDisplay();
  }

  pointerDown(pointer) {
    if (!this.object || this.dragging || pointer.button !== 0) return;

    _raycaster.setFromCamera(pointer, this.camera);
    const intersect = intersectObjectWithRay(this._gizmo.picker, _raycaster);

    if (!intersect) return;

    const geo = intersect.object.geometry;

    if (geo instanceof SphereGeometry) {
      this.mode = 'scale';
    } else if (
      geo instanceof LatheGeometry ||
      geo instanceof CylinderGeometry
    ) {
      this.mode = 'translate';
    } else if (geo instanceof TorusGeometry) {
      this.mode = 'rotate';
    } else {
      return;
    }

    this.axis = intersect.object.name.split('-')[0] || null;
    this._updateGizmoHighlight();

    const planeIntersect = intersectObjectWithRay(
      this._plane,
      _raycaster,
      true
    );
    if (!planeIntersect) {
      this.axis = null;
      this._updateGizmoHighlight();
      return;
    }

    this.object.updateMatrixWorld();
    this.object.parent?.updateMatrixWorld();

    this._positionStart.copy(this.object.position);
    this._quaternionStart.copy(this.object.quaternion);
    this._scaleStart.copy(this.object.scale);

    this.object.matrixWorld.decompose(
      this.worldPositionStart,
      this.worldQuaternionStart,
      this._worldScaleStart
    );

    this.pointStart.copy(planeIntersect.point).sub(this.worldPositionStart);

    if (this.mode === 'rotate') this._gizmo.startAngle = 0;

    this.dragging = true;
    _mouseDownEvent.mode = this.mode;
    this.dispatchEvent(_mouseDownEvent);
    this._createPointLineGeometry();
    this._syncSelectionBoxTransform();
    this._boxGroup.updateMatrixWorld(true);

    this._updateSpheresScale();

    this._boxGroup.updateMatrixWorld(true);

    this.updateAngleDisplay();
  }

  pointerMove(pointer) {
    const axis = this.axis;
    const mode = this.mode;
    const object = this.object;

    if (!object || !axis || !this.dragging || pointer.button !== -1) return;

    _raycaster.setFromCamera(pointer, this.camera);
    const planeIntersect = intersectObjectWithRay(
      this._plane,
      _raycaster,
      true
    );
    if (!planeIntersect) return;

    this.pointEnd.copy(planeIntersect.point).sub(this.worldPositionStart);

    if (mode === 'translate') {
      this._offset.copy(this.pointEnd).sub(this.pointStart);
      this._offset.applyQuaternion(this._worldQuaternionInv);
      if (axis.indexOf('X') === -1) this._offset.x = 0;
      if (axis.indexOf('Y') === -1) this._offset.y = 0;
      if (axis.indexOf('Z') === -1) this._offset.z = 0;
      this._offset.applyQuaternion(this.worldQuaternion);
      this._offset.divide(this._parentScale);
      object.position.copy(this._positionStart).add(this._offset);

      if (this.translationSnap) {
        object.position.applyQuaternion(
          _tempQuaternion.copy(this.worldQuaternion).invert()
        );
        if (axis.includes('X'))
          object.position.x =
            Math.round(object.position.x / this.translationSnap) *
            this.translationSnap;
        if (axis.includes('Y'))
          object.position.y =
            Math.round(object.position.y / this.translationSnap) *
            this.translationSnap;
        if (axis.includes('Z'))
          object.position.z =
            Math.round(object.position.z / this.translationSnap) *
            this.translationSnap;
        object.position.applyQuaternion(this.worldQuaternion);
      }

      this._refreshSelectionBoxGeometry();
    } else if (mode === 'scale') {
      // Prevent light assets from being scaled with transform controls
      const isLight =
        object.isLight ||
        object.type === 'AmbientLight' ||
        object.type === 'DirectionalLight' ||
        object.type === 'PointLight' ||
        object.type === 'SpotLight' ||
        object.type === 'RectAreaLight' ||
        // Also check for light helper types
        object.type === 'AmbientLightHelper' ||
        object.type === 'DirectionalLightHelper' ||
        object.type === 'PointLightHelper' ||
        object.type === 'SpotLightHelper' ||
        object.type === 'AreaLightHelper';

      if (!isLight) {
        if (axis === 'XYZ') {
          let d = this.pointEnd.length() / this.pointStart.length();
          if (this.pointEnd.dot(this.pointStart) < 0) d *= -1;
          d = 1 + (d - 1) * 0.025;
          _tempVector2.set(d, d, d);
        } else {
          _tempVector
            .copy(this.pointStart)
            .applyQuaternion(this._worldQuaternionInv);
          _tempVector2
            .copy(this.pointEnd)
            .applyQuaternion(this._worldQuaternionInv)
            .divide(_tempVector);

          if (!axis.includes('X')) _tempVector2.x = 1;
          if (!axis.includes('Y')) _tempVector2.y = 1;
          if (!axis.includes('Z')) _tempVector2.z = 1;
        }

        object.scale.copy(this._scaleStart).multiply(_tempVector2);

        if (this.scaleSnap) {
          if (axis.includes('X'))
            object.scale.x =
              Math.round(object.scale.x / this.scaleSnap) * this.scaleSnap ||
              this.scaleSnap;
          if (axis.includes('Y'))
            object.scale.y =
              Math.round(object.scale.y / this.scaleSnap) * this.scaleSnap ||
              this.scaleSnap;
          if (axis.includes('Z'))
            object.scale.z =
              Math.round(object.scale.z / this.scaleSnap) * this.scaleSnap ||
              this.scaleSnap;
        }
      }

      this._refreshSelectionBoxGeometry();
    } else if (mode === 'rotate') {
      this._offset.copy(this.pointEnd).sub(this.pointStart);

      const dist = this.worldPosition.distanceTo(
        _tempVector.setFromMatrixPosition(this.camera.matrixWorld)
      );
      const speed = 20 / dist;

      let inPlane = false;

      if (['X', 'Y', 'Z'].includes(axis)) {
        const unitAxis = _unit[axis];
        this.rotationAxis.copy(unitAxis);
        _tempVector.copy(unitAxis).applyQuaternion(this.worldQuaternionStart);
        _tempVector.cross(this.eye);

        if (_tempVector.lengthSq() < 1e-6) {
          inPlane = true;
        } else {
          this.rotationAngle =
            this._offset.dot(_tempVector.normalize()) * speed;
        }
      } else if (['XY', 'YZ', 'XZ'].includes(axis)) {
        let unitAxis = new Vector3();
        if (axis === 'XY') unitAxis.copy(_unit.Z);
        else if (axis === 'YZ') unitAxis.copy(_unit.X);
        else if (axis === 'XZ') unitAxis.copy(_unit.Y);

        this.rotationAxis.copy(unitAxis);
        _tempVector.copy(unitAxis).applyQuaternion(this.worldQuaternionStart);
        _tempVector.cross(this.eye);

        if (_tempVector.lengthSq() < 1e-6) {
          inPlane = true;
        } else {
          this.rotationAngle =
            this._offset.dot(_tempVector.normalize()) * speed;
        }
      } else if (axis === 'XYZE') {
        this.rotationAxis.copy(this._offset).cross(this.eye).normalize();
        this.rotationAngle =
          this._offset.dot(
            _tempVector.copy(this.rotationAxis).cross(this.eye)
          ) * speed;
      }

      if (axis === 'E' || inPlane) {
        this.rotationAxis.copy(this.eye);
        this.rotationAngle = this.pointEnd.angleTo(this.pointStart);
        this._startNorm.copy(this.pointStart).normalize();
        this._endNorm.copy(this.pointEnd).normalize();
        if (this._endNorm.cross(this._startNorm).dot(this.eye) < 0)
          this.rotationAngle *= -1;
      }

      if (this.rotationSnap) {
        this.rotationAngle =
          Math.round(this.rotationAngle / this.rotationSnap) *
          this.rotationSnap;
      }

      if (['X', 'Y', 'Z'].includes(axis)) {
        const rotQuat = _tempQuaternion.setFromAxisAngle(
          _unit[axis],
          this.rotationAngle
        );
        object.quaternion
          .copy(this._quaternionStart)
          .multiply(rotQuat)
          .normalize();
      } else if (['XY', 'YZ', 'XZ'].includes(axis)) {
        let localAxis;
        if (axis === 'XY') localAxis = _unit.Z;
        else if (axis === 'YZ') localAxis = _unit.X;
        else if (axis === 'XZ') localAxis = _unit.Y;

        const rotQuat = _tempQuaternion.setFromAxisAngle(
          localAxis,
          this.rotationAngle
        );
        object.quaternion
          .copy(this._quaternionStart)
          .multiply(rotQuat)
          .normalize();
      } else {
        this.rotationAxis.applyQuaternion(this._parentQuaternionInv);
        const rotQuat = _tempQuaternion.setFromAxisAngle(
          this.rotationAxis,
          this.rotationAngle
        );
        object.quaternion
          .copy(rotQuat)
          .multiply(this._quaternionStart)
          .normalize();
      }

      object.updateMatrixWorld();
      this._refreshSelectionBoxGeometry();
    }

    this.dispatchEvent(_changeEvent);
    this.dispatchEvent(_objectChangeEvent);
    this.updateAngleDisplay();
  }

  pointerUp(pointer) {
    if (pointer.button !== 0) return;

    if (this.dragging && this.axis) {
      _mouseUpEvent.mode = this.mode;
      this.dispatchEvent(_mouseUpEvent);
    }

    this.dragging = false;
    this.hoveredPartName = null;
    this.axis = null;
    this._updateGizmoHighlight();
    this.updateAngleDisplay();
  }

  dispose() {
    this.domElement.removeEventListener('pointerdown', this._onPointerDown);
    this.domElement.removeEventListener('pointermove', this._onPointerHover);
    this.domElement.removeEventListener('pointermove', this._onPointerMove);
    this.domElement.removeEventListener('pointerup', this._onPointerUp);

    this.traverse((child) => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) child.material.dispose();
    });

    if (this._boxGroup) {
      if (this._points) {
        this._points.forEach((p) => {
          this._boxGroup.remove(p);
          p.geometry?.dispose();
          p.material?.dispose();
        });
      }
      if (this._lines) {
        this._boxGroup.remove(this._lines);
        this._lines.geometry?.dispose();
        this._lines.material?.dispose();
      }
      this.remove(this._boxGroup);
    }

    if (this._angleDisplay) {
      this._angleDisplay.remove();
    }
  }

  _isAttachedObjectVisible() {
    if (!this.object) return false;
    if (this.object.userData?.editorVisible === false) return false;

    const isParticleRoot =
      this.object.deleteType === 'particle' ||
      [
        'foliage',
        'clouds',
        'rain',
        'snow',
        'fire',
        'stars',
        'water',
        'terrain',
      ].includes(this.object.objType);

    if (isParticleRoot) {
      return true;
    }

    if (this.object.visible === false) return false;
    return true;
  }

  refreshSelectionBounds() {
    this._refreshSelectionBoxGeometry();
  }

  attach(object) {
    if (!object) {
      this.object = undefined;
      this.visible = false;
      this._boxGroup.visible = false;
      this.hoveredPartName = null;
      this.axis = null;
      this._updateGizmoHighlight();
      return this;
    }

    this.object = object;
    this.visible = this._isAttachedObjectVisible();

    // Check if it's a light or light helper
    const isLight =
      object.isLight ||
      object.type === 'AmbientLight' ||
      object.type === 'DirectionalLight' ||
      object.type === 'PointLight' ||
      object.type === 'SpotLight' ||
      object.type === 'RectAreaLight' ||
      object.type === 'AmbientLightHelper' ||
      object.type === 'DirectionalLightHelper' ||
      object.type === 'PointLightHelper' ||
      object.type === 'SpotLightHelper' ||
      object.type === 'AreaLightHelper';

    if (this.showSelectionBox && !isLight && this.visible) {
      this._lastBoxRecomputeTime = Date.now();
      this._unscaledBoxSize.copy(this._computeUnscaledBoundingBox(object));
      this._attachedObjectGeometryId = object.geometry?.id;
      this._lastObjectId = object.id;
      this._lastBoundsRootId = (
        resolveSelectionBoundsRoot(object) || object
      ).uuid;
      this._lastQuaternion = object.quaternion.clone();
      this._lastPosition = object.position.clone();
      this._lastScale = object.scale.clone();

      this._createPointLineGeometry();
      this._syncSelectionBoxTransform();
      this._boxGroup.visible = true;
    } else {
      this._boxGroup.visible = false;
    }

    this._updateGizmoHighlight();

    return this;
  }

  detach() {
    this.object = undefined;
    this.visible = false;
    this.hoveredPartName = null;
    this.axis = null;
    this._boxGroup.visible = false;
    this._attachedObjectGeometryId = null;
    this._angleDisplay.style.display = 'none';
    this._updateGizmoHighlight();
    return this;
  }

  reset() {
    if (!this.enabled || !this.dragging) return;
    this.object.position.copy(this._positionStart);
    this.object.quaternion.copy(this._quaternionStart);
    this.object.scale.copy(this._scaleStart);
    this.dispatchEvent(_changeEvent);
    this.dispatchEvent(_objectChangeEvent);
    this.updateAngleDisplay();
  }

  getMode() {
    return this.mode;
  }
  setMode(mode) {
    this.mode = mode;
    this._updateGizmoHighlight();
    this.updateAngleDisplay();
  }
  setTranslationSnap(v) {
    this.translationSnap = v;
  }
  setRotationSnap(v) {
    this.rotationSnap = v;
  }
  setScaleSnap(v) {
    this.scaleSnap = v;
  }
  setSize(v) {
    this.size = v;
  }
  setSpace(v) {
    this.space = v;
    this.updateAngleDisplay();
  }

  _updateSpheresScale() {
    if (!this._points || !this.object || !this.camera) return;

    this.camera.updateMatrixWorld();
    this.camera.matrixWorld.decompose(
      this.cameraPosition,
      this.cameraQuaternion,
      this._cameraScale
    );

    // ✅ NEW: Scale corner spheres consistently regardless of object scale
    // The _boxGroup already has object.scale applied, so spheres need inverse scale
    this._points.forEach((p) => {
      const scaleFactor = computeTransformControlsScaleFactor(
        this.camera,
        this.worldPosition,
        this.cameraPosition,
        this.size,
        1
      );

      // Inverse scale to keep sphere size constant (use box group scale, not gizmo target)
      const sx =
        this._boxGroup.scale.x === 0 ? 1 : Math.abs(this._boxGroup.scale.x);
      const sy =
        this._boxGroup.scale.y === 0 ? 1 : Math.abs(this._boxGroup.scale.y);
      const sz =
        this._boxGroup.scale.z === 0 ? 1 : Math.abs(this._boxGroup.scale.z);

      p.scale.set(scaleFactor / sx, scaleFactor / sy, scaleFactor / sz);
    });
  }
}

function getPointer(event) {
  if (this.domElement.ownerDocument.pointerLockElement) {
    return { x: 0, y: 0, button: event.button };
  }
  const rect = this.domElement.getBoundingClientRect();
  return {
    x: ((event.clientX - rect.left) / rect.width) * 2 - 1,
    y: (-(event.clientY - rect.top) / rect.height) * 2 + 1,
    button: event.button,
  };
}

function onPointerHover(event) {
  if (!this.enabled) return;
  if (['mouse', 'pen'].includes(event.pointerType)) {
    this.pointerHover(this._getPointer(event));
  }
}

function onPointerDown(event) {
  if (!this.enabled) return;
  if (!document.pointerLockElement)
    this.domElement.setPointerCapture(event.pointerId);
  this.domElement.addEventListener('pointermove', this._onPointerMove);
  this.pointerHover(this._getPointer(event));
  this.pointerDown(this._getPointer(event));
}

function onPointerMove(event) {
  if (this.enabled) this.pointerMove(this._getPointer(event));
}

function onPointerUp(event) {
  if (!this.enabled) return;
  this.domElement.releasePointerCapture(event.pointerId);
  this.domElement.removeEventListener('pointermove', this._onPointerMove);
  this.pointerUp(this._getPointer(event));
}

function intersectObjectWithRay(object, raycaster, includeInvisible = false) {
  const intersects = raycaster.intersectObject(object, true);
  for (const i of intersects) {
    if (i.object.visible || includeInvisible) return i;
  }
  return false;
}

// ────────────────────────────────────────────────
// Gizmo
// ────────────────────────────────────────────────

class TransformControlsGizmo extends Object3D {
  constructor() {
    super();
    this.isTransformControlsGizmo = true;
    this.type = 'TransformControlsGizmo';
    this.startAngle = 0;

    const gizmoMaterial = new THREE.MeshBasicMaterial({
      depthTest: false,
      depthWrite: false,
      fog: false,
      toneMapped: false,
      transparent: true,
    });
    const lineMaterial = new LineBasicMaterial({
      depthTest: false,
      depthWrite: false,
      fog: false,
      toneMapped: false,
      transparent: true,
    });

    const matInvisible = gizmoMaterial.clone();
    matInvisible.opacity = 0;
    const matRed = gizmoMaterial.clone();
    matRed.color.setHex(0xff4a56);
    const matGreen = gizmoMaterial.clone();
    matGreen.color.setHex(0x00e6b0);
    const matBlue = gizmoMaterial.clone();
    matBlue.color.setHex(0x009cff);
    const matGrayTransparent = gizmoMaterial.clone();
    matGrayTransparent.color.setHex(0x696969);
    matGrayTransparent.opacity = 0.4;
    const matYellow = gizmoMaterial.clone();
    matYellow.color.setHex(0xffff00);
    const matYellowTransparent = gizmoMaterial.clone();
    matYellowTransparent.color.setHex(0xffff00);
    matYellowTransparent.opacity = 0.25;

    const scale = 2.5;
    const arrowPoints = [
      new Vector2(0.002 * scale, 0.049 * scale),
      new Vector2(0.004 * scale, 0.042 * scale),
      new Vector2(0.0061 * scale, 0.034 * scale),
      new Vector2(0.0083 * scale, 0.026 * scale),
      new Vector2(0.0095 * scale, 0.018 * scale),
      new Vector2(0.012 * scale, 0.0095 * scale),
      new Vector2(0.013 * scale, 0.0045 * scale),
      new Vector2(0.0065 * scale, 0.0015 * scale),
      new Vector2(0, 0),
    ];

    const arrowGeo = new LatheGeometry(arrowPoints, 15);
    const sphereGeo = new SphereGeometry(0.04, 16, 12);
    const shaftGeo = new CylinderGeometry(0.01, 0.01, 0.5, 4);
    shaftGeo.translate(0, 0.25, 0);

    const torusGeo = new TorusGeometry(0.3, 0.01, 4, 64, (Math.PI * 2) / 4);
    const meterGeo = new TorusGeometry(0.4, 0.005, 4, 64, Math.PI * 2);

    const needleGeo = new CylinderGeometry(0.002, 0.002, 0.38, 8);
    needleGeo.rotateZ(Math.PI / 2);
    needleGeo.translate(0.19, 0, 0);

    const needleMaterial = new LineBasicMaterial({
      color: 0xff3333,
      transparent: true,
      opacity: 1.0,
    });
    needleMaterial.isAngleNeedle = true;

    const gizmoMap = {
      X: [
        [
          new Mesh(arrowGeo, matRed.clone()),
          [0.5, 0, 0],
          [0, 0, -Math.PI / 2],
          null,
          'X-arrow',
        ],
        [
          new Mesh(sphereGeo, matRed.clone()),
          [0.35, 0, 0],
          null,
          null,
          'X-sphere',
        ],
        [
          new Mesh(shaftGeo, matRed.clone()),
          [0, 0, 0],
          [0, 0, -Math.PI / 2],
          null,
          'X-arrow',
        ],
      ],
      Y: [
        [
          new Mesh(arrowGeo, matGreen.clone()),
          [0, 0.5, 0],
          null,
          null,
          'Y-arrow',
        ],
        [
          new Mesh(sphereGeo, matGreen.clone()),
          [0, 0.35, 0],
          null,
          null,
          'Y-sphere',
        ],
        [new Mesh(shaftGeo, matGreen.clone()), null, null, null, 'Y-arrow'],
      ],
      Z: [
        [
          new Mesh(arrowGeo, matBlue.clone()),
          [0, 0, 0.5],
          [Math.PI / 2, 0, 0],
          null,
          'Z-arrow',
        ],
        [
          new Mesh(sphereGeo, matBlue.clone()),
          [0, 0, 0.35],
          null,
          null,
          'Z-sphere',
        ],
        [
          new Mesh(shaftGeo, matBlue.clone()),
          null,
          [Math.PI / 2, 0, 0],
          null,
          'Z-arrow',
        ],
      ],
      XYZ: [
        [
          new Mesh(
            new OctahedronGeometry(0.1, 0),
            matYellowTransparent.clone()
          ),
          [0, 0, 0],
          null,
          null,
          'XYZ',
        ],
      ],
      XY: [
        [
          new Mesh(
            new CylinderGeometry(0.05, 0.05, 0.001, 8),
            matGrayTransparent.clone()
          ),
          [0.1, 0.1, 0],
          [Math.PI / 2, 0, 0],
        ],
        [new Mesh(torusGeo, matBlue.clone()), [0.05, 0.05, 0], [0, 0, 0]],
      ],
      YZ: [
        [
          new Mesh(
            new CylinderGeometry(0.05, 0.05, 0.001, 8),
            matGrayTransparent.clone()
          ),
          [0, 0.1, 0.1],
          [0, 0, Math.PI / 2],
        ],
        [
          new Mesh(torusGeo, matRed.clone()),
          [0, 0.05, 0.05],
          [0, -Math.PI / 2, 0],
        ],
      ],
      XZ: [
        [
          new Mesh(
            new CylinderGeometry(0.05, 0.05, 0.001, 8),
            matGrayTransparent.clone()
          ),
          [0.1, 0, 0.1],
          [0, 0, 0],
        ],
        [
          new Mesh(torusGeo, matGreen.clone()),
          [0.05, 0, 0.05],
          [Math.PI / 2, 0, 0],
        ],
      ],
      ANGLE: [
        [
          new Mesh(meterGeo, matYellow.clone()),
          [0, 0, 0],
          null,
          null,
          'angleMeter',
        ],
        [
          new Line(needleGeo, matRed.clone()),
          [0, 0, 0],
          null,
          null,
          'angleNeedle',
        ],
        [
          new Mesh(new SphereGeometry(0.025, 12, 12), matYellow.clone()),
          [0.4, 0, 0],
          null,
          null,
          'angleSphere',
        ],
        [
          new Mesh(new BufferGeometry(), matYellowTransparent.clone()),
          [0, 0, 0],
          null,
          null,
          'angleArc',
        ],
      ],
    };

    const pickerMap = {
      X: [
        [
          new Mesh(new CylinderGeometry(0.05, 0.05, 0.5, 8), matInvisible),
          [0.45, 0, 0],
          [0, 0, Math.PI / 2],
          null,
          'X-arrow',
        ],
        [
          new Mesh(new SphereGeometry(0.08, 12, 12), matInvisible),
          [0.35, 0, 0],
          null,
          null,
          'X-sphere',
        ],
      ],
      Y: [
        [
          new Mesh(new CylinderGeometry(0.05, 0.05, 0.5, 8), matInvisible),
          [0, 0.45, 0],
          null,
          null,
          'Y-arrow',
        ],
        [
          new Mesh(new SphereGeometry(0.08, 12, 12), matInvisible),
          [0, 0.35, 0],
          null,
          null,
          'Y-sphere',
        ],
      ],
      Z: [
        [
          new Mesh(new CylinderGeometry(0.05, 0.05, 0.5, 8), matInvisible),
          [0, 0, 0.45],
          [-Math.PI / 2, 0, 0],
          null,
          'Z-arrow',
        ],
        [
          new Mesh(new SphereGeometry(0.08, 12, 12), matInvisible),
          [0, 0, 0.35],
          null,
          null,
          'Z-sphere',
        ],
      ],
      XY: [
        [
          new Mesh(
            new TorusGeometry(0.3, 0.04, 4, 32, (Math.PI * 2) / 4),
            matInvisible
          ),
          [0.05, 0.05, 0],
          [0, 0, 0],
        ],
      ],
      YZ: [
        [
          new Mesh(
            new TorusGeometry(0.3, 0.04, 4, 32, (Math.PI * 2) / 4),
            matInvisible
          ),
          [0, 0.05, 0.05],
          [0, -Math.PI / 2, 0],
        ],
      ],
      XZ: [
        [
          new Mesh(
            new TorusGeometry(0.3, 0.04, 4, 32, (Math.PI * 2) / 4),
            matInvisible
          ),
          [0.05, 0, 0.05],
          [Math.PI / 2, 0, 0],
        ],
      ],
    };

    function setup(map) {
      const g = new Object3D();
      for (const name in map) {
        map[name].forEach(([mesh, pos, rot, scl, tag]) => {
          const m = mesh.clone();
          m.name = tag || name;
          m.tag = tag;
          if (pos) m.position.set(...pos);
          if (rot) m.rotation.set(...rot);
          if (scl) m.scale.set(...scl);
          m.updateMatrix();
          if (m.geometry) {
            const geom = m.geometry.clone();
            geom.applyMatrix4(m.matrix);
            m.geometry = geom;
          }
          m.position.set(0, 0, 0);
          m.rotation.set(0, 0, 0);
          m.scale.set(1, 1, 1);
          m.renderOrder = 1000;
          g.add(m);
        });
      }
      return g;
    }

    this.gizmo = setup(gizmoMap);
    this.picker = setup(pickerMap);

    this.add(this.gizmo);
    this.add(this.picker);

    this.picker.visible = false;
    disableMeshShadows(this);
  }

  updateMatrixWorld(force) {
    this.position.copy(this.worldPosition);
    this.gizmo.visible = true;

    const scaleFactor = computeTransformControlsScaleFactor(
      this.camera,
      this.worldPosition,
      this.cameraPosition,
      this.parent?.size,
      1.4
    );

    this.gizmo.scale.setScalar(scaleFactor);
    this.picker.scale.setScalar(scaleFactor);

    const gizmoQuaternion = this.parent?.worldQuaternion || new Quaternion();

    this.gizmo.children.forEach((child) => {
      if (
        child.tag &&
        ['angleMeter', 'angleNeedle', 'angleArc', 'angleSphere'].includes(
          child.tag
        )
      )
        return;
      child.quaternion.copy(gizmoQuaternion);
    });

    this.picker.children.forEach((child) => {
      child.quaternion.copy(gizmoQuaternion);
    });

    const isActiveRotation =
      this.parent?.mode === 'rotate' &&
      this.parent?.dragging === true &&
      this.parent?.axis &&
      !['E', 'XYZE'].includes(this.parent?.axis);

    this.gizmo.children.forEach((child) => {
      if (
        child.tag &&
        ['angleMeter', 'angleNeedle', 'angleArc', 'angleSphere'].includes(
          child.tag
        )
      )
        return;
      child.visible = !isActiveRotation;
    });

    this.picker.visible = !isActiveRotation;

    const angleParts = this.gizmo.children.filter(
      (c) =>
        c.tag &&
        ['angleMeter', 'angleNeedle', 'angleArc', 'angleSphere'].includes(c.tag)
    );

    const shouldShowAngle = isActiveRotation;

    angleParts.forEach((part) => {
      part.visible = shouldShowAngle;

      if (!shouldShowAngle) {
        if (part.tag === 'angleArc') {
          part.geometry?.dispose();
          part.geometry = new BufferGeometry();
        }
        return;
      }

      let baseQuat = gizmoQuaternion.clone();

      let axisQ = new Quaternion();
      if (this.parent.axis === 'X')
        axisQ.setFromEuler(new Euler(0, Math.PI / 2, 0));
      else if (this.parent.axis === 'Y')
        axisQ.setFromEuler(new Euler(Math.PI / 2, 0, 0));
      else if (this.parent.axis === 'Z') axisQ.setFromEuler(new Euler(0, 0, 0));
      else if (this.parent.axis === 'XY')
        axisQ.setFromEuler(new Euler(0, 0, 0));
      else if (this.parent.axis === 'YZ')
        axisQ.setFromEuler(new Euler(0, Math.PI / 2, 0));
      else if (this.parent.axis === 'XZ')
        axisQ.setFromEuler(new Euler(-Math.PI / 2, 0, 0));

      part.quaternion.copy(baseQuat).multiply(axisQ);

      if (['angleNeedle', 'angleSphere', 'angleMeter'].includes(part.tag)) {
        const rot = new Quaternion().setFromAxisAngle(
          new Vector3(0, 0, 1),
          this.parent.rotationAngle || 0
        );
        part.quaternion.multiply(rot);
      }

      if (part.tag === 'angleArc') {
        const geo = generateArcGeometry(0, this.parent.rotationAngle || 0);
        part.geometry?.dispose();
        part.geometry = geo;
        part.material.color.setHex(0xffff00);
        part.renderOrder = 2000;
      }
    });

    super.updateMatrixWorld(force);
  }
}

function generateArcGeometry(start, end, radius = 0.4, segments = 64) {
  const positions = [0, 0, 0];
  let s = start % (Math.PI * 2);
  if (s < 0) s += Math.PI * 2;
  let e = end;
  let delta = e - s;
  if (Math.abs(delta) < 0.001) delta = delta >= 0 ? 0.001 : -0.001;

  const steps = Math.max(
    segments,
    Math.ceil((Math.abs(delta) * segments) / (Math.PI * 2))
  );

  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const ang = s + t * delta;
    positions.push(radius * Math.cos(ang), radius * Math.sin(ang), 0);
  }

  const indices = [];
  for (let i = 1; i < positions.length / 3; i++) {
    indices.push(0, i, i + 1);
  }

  const geo = new BufferGeometry();
  geo.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

class TransformControlsPlane extends Mesh {
  constructor() {
    super(
      new PlaneGeometry(100000, 100000, 2, 2),
      new MeshBasicMaterial({
        visible: false,
        wireframe: true,
        side: DoubleSide,
        transparent: true,
        opacity: 0.1,
      })
    );
    this.isTransformControlsPlane = true;
    this.type = 'TransformControlsPlane';
    this.castShadow = false;
    this.receiveShadow = false;
  }

  updateMatrixWorld(force) {
    const v1 = new Vector3();
    const v2 = new Vector3();
    const v3 = new Vector3();
    const alignVector = new Vector3();
    const tempMatrix = new Matrix4();
    const dir = new Vector3();

    this.position.copy(this.worldPosition);

    const effectiveSpace =
      this.mode === 'translate' || this.mode === 'scale' ? 'local' : this.space;

    v1.copy(_unit.X).applyQuaternion(
      effectiveSpace === 'local' ? this.worldQuaternion : new Quaternion()
    );
    v2.copy(_unit.Y).applyQuaternion(
      effectiveSpace === 'local' ? this.worldQuaternion : new Quaternion()
    );
    v3.copy(_unit.Z).applyQuaternion(
      effectiveSpace === 'local' ? this.worldQuaternion : new Quaternion()
    );

    alignVector.copy(v2);

    if (this.mode === 'translate' || this.mode === 'scale') {
      switch (this.axis) {
        case 'X':
          alignVector.copy(this.eye).cross(v1);
          dir.copy(v1).cross(alignVector);
          break;
        case 'Y':
          alignVector.copy(this.eye).cross(v2);
          dir.copy(v2).cross(alignVector);
          break;
        case 'Z':
          alignVector.copy(this.eye).cross(v3);
          dir.copy(v3).cross(alignVector);
          break;
        case 'XY':
          dir.copy(v3);
          break;
        case 'YZ':
          dir.copy(v1);
          break;
        case 'XZ':
          dir.copy(v2);
          break;
        default:
          dir.set(0, 0, 0);
      }
    }

    if (dir.lengthSq() === 0) {
      this.quaternion.copy(this.cameraQuaternion);
    } else {
      tempMatrix.lookAt(new Vector3(), dir, alignVector);
      this.quaternion.setFromRotationMatrix(tempMatrix);
    }

    super.updateMatrixWorld(force);
  }
}

export { TransformControls, TransformControlsGizmo, TransformControlsPlane };
